import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Copy, Check, AtSign, Trash2, AlertTriangle } from 'lucide-react';
import ProfileForm from './ProfileForm';
import UsernameModal from './UsernameModal';
import { deletePublicProfile } from '../services/profileService';
import { useAuth } from '../contexts/AuthContext';

export default function ProfileEditModal({ username, profile, isOpen, onClose, onSaved, onDeleted }) {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    if (isOpen) {
      document.documentElement.style.overflow = 'hidden';
      return () => { document.documentElement.style.overflow = ''; };
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handle = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const profileUrl = `markersync.com/profile/${username}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(`https://${profileUrl}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError('');
    const result = await deletePublicProfile(username, user.uid);
    setDeleting(false);
    if (result.success) {
      onDeleted();
    } else {
      setDeleteError(result.error || 'Failed to delete profile.');
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[60] backdrop-blur-sm bg-black/30 flex items-center justify-center p-4 animate-fadeOnly"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl border border-slate-100 p-6 animate-slideUp flex flex-col" style={{ maxHeight: '90vh' }}>

        {/* Header */}
        <div className="flex items-start justify-between mb-5">
          <div>
            <h2 className="text-xl font-bold text-slate-900">Edit profile</h2>
            <div className="w-12 h-1 bg-gradient-to-r from-amber-400 to-orange-400 rounded-full mt-2" />
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Public URL */}
        <div className="flex items-center justify-between gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 mb-5">
          <span className="text-xs font-mono text-slate-500 truncate">{profileUrl}</span>
          <button
            onClick={handleCopy}
            className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-800 transition-colors"
          >
            {copied ? <Check size={13} className="text-emerald-500" /> : <Copy size={13} />}
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>

        {/* Form */}
        <ProfileForm
          username={username}
          initialData={profile}
          onDone={(savedData) => { onSaved(savedData); onClose(); }}
          saveLabel="Save changes"
          isEdit
        />

        {/* Change username + Delete profile */}
        <div className="border-t border-slate-100 mt-4 pt-4 space-y-2">
          <button
            onClick={() => setShowUsernameModal(true)}
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-50 rounded-xl border border-slate-200 hover:border-slate-300 transition-all"
          >
            <AtSign size={13} />
            Change username
          </button>

          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-red-400 hover:text-red-600 hover:bg-red-50 rounded-xl border border-red-100 hover:border-red-200 transition-all"
            >
              <Trash2 size={13} />
              Delete profile
            </button>
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 space-y-2">
              <div className="flex items-start gap-2">
                <AlertTriangle size={14} className="text-red-500 shrink-0 mt-0.5" />
                <p className="text-xs text-red-700 leading-relaxed">
                  This will permanently delete your public profile. Your palettes in the gallery won't be affected.
                </p>
              </div>
              {deleteError && <p className="text-xs text-red-600">{deleteError}</p>}
              <div className="flex gap-2">
                <button
                  onClick={() => { setShowDeleteConfirm(false); setDeleteError(''); }}
                  disabled={deleting}
                  className="flex-1 px-3 py-1.5 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex-1 px-3 py-1.5 text-xs font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors disabled:opacity-50"
                >
                  {deleting ? 'Deleting…' : 'Yes, delete'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <UsernameModal isOpen={showUsernameModal} onClose={() => setShowUsernameModal(false)} />
    </div>,
    document.body
  );
}
